import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { IMainSearchResultList } from '@/components/common/MainSearch/MainSearchResult';
import { CHARS_COUNT_TO_START_SEARCHING } from '@/config/common.config';
import { Search, SearchVariables } from '@/graphql/contracts/_generated_/Search.query';
import SearchQuery from '@/graphql/contracts/Search.query.gql';
import { useEffectExceptOnMount } from '@/hooks/useEffectExceptOnMount.hook';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import { notificationService } from '@/services/notification/notification.client';
import { normalizeSearchResultsForComponent } from '../components/normalizeSearchResults';
import { contractsBCPath } from '@/utils/path.utlis';

export default function useSearchContracts(
  handleChangeCustomerID: (customerName: string | null) => void,
  searchedQuery?: string
) {
  const history = useHistory<History>();
  const [searchResult, setSearchResult] = useState<IMainSearchResultList[]>([]);
  const [searchString, setSearchString] = useState(searchedQuery ?? '');

  const [_getSearchResults, { loading }] = useLazyQuery<Search, SearchVariables>(
    SearchQuery,
    {
      client: apolloClientContracts,
      fetchPolicy: 'network-only',
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      onCompleted: (data) => {
        if (!data || !data.searchContractsAndCustomers) return;

        const result = normalizeSearchResultsForComponent({
          inputData: data.searchContractsAndCustomers,
          searchString,
          handleClickContract: (response) => {
            history.push(getParameterizedLink(`/${contractsBCPath}/${response.contractID}`));
          },
          handleClickCustomer: (response) => {
            handleChangeCustomerID(response.customerName);
          }
        });

        setSearchResult(result);
      },
    }
  );

  const getSearchResults = (str: string) => {
    setSearchString(str);
  };

  useEffectExceptOnMount(() => {
    if (searchString?.length >= CHARS_COUNT_TO_START_SEARCHING) {
      _getSearchResults({
        variables: { searchString },
      });
    } else {
      setSearchResult([]);

      if (searchString?.length === 0) {
        handleChangeCustomerID(null);
      }
    }
  }, [searchString]);

  return {
    getSearchResults,
    isLoadingSearchContracts: loading,
    searchResult,
    searchString
  } as const;
}
